import { Button, Heading } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { v4 } from 'uuid';

import { TextField } from 'src/components/TextField';
import { FormBodyAPI } from 'src/constants';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import { CreateServicePopover } from 'src/routes/payments/components/CreateServicePopover';
import { LineItemFormSchema } from 'src/routes/payments/components/lineItem-editor/lineItem/Controller';
import {
  LineItemEditor,
  LineItemEditorPricingMode,
} from 'src/routes/payments/components/lineItem-editor/LineItemEditor';
import { ManageManualPaymentSetting } from 'src/routes/payments/components/ManageManualPaymentSetting';
import { ServiceDetailsSchema } from 'src/routes/payments/services/service-details/components/EditServiceDetails';
import {
  ProductPriceTableRowModal,
  ServiceSchema,
} from 'src/routes/payments/services/types';
import { ProductStorePreview } from 'src/routes/payments/store/components/Preview/ProductStorePreview';
import { ProductStoreLineItemCard } from 'src/routes/payments/store/form/components/ProductStoreLineItemCard';
import { ProductStorePresentation } from 'src/routes/payments/store/form/components/ProductStorePresentation';
import {
  ProductStoreLineItem,
  ProductStoreSchema,
} from 'src/routes/payments/store/types/schema';
import { getUpdatedLineItemAfterProductCreated } from 'src/routes/payments/utils/lineItem';
import { LineItemType } from 'src/services/api/invoicesApi';
import {
  CreatePriceInput,
  Product,
  useAddPriceByProductIdMutation,
  useAddProductMutation,
  useDeletePriceMutation,
  useGetPricesByPortalQuery,
  useGetProductsQuery,
  useUpdateProductMutation,
} from 'src/services/api/productsApi';
import InvoiceUtils from 'src/utils/InvoiceUtils';

export enum ActiveFormSectionType {
  DisplayName = 'displayName',
  Subscriptions = 'subscriptions',
  OneTimeProducts = 'oneTimeProducts',
}

const TAX_PERCENTAGE = '0';
const MAX_ONE_TIME_PRODUCTS = 12;
const MAX_SUBSCRIPTIONS_PRODUCTS = 4;

type ProductFormType<Mode> = Mode extends 'edit'
  ? ServiceDetailsSchema
  : ServiceSchema;

const getFormattedLineItems = (lineItems: ProductStoreLineItem[]) => {
  return lineItems.map((item) => ({
    id: item.id || '',
    name: item.name || '',
    productId: item.productId || '',
    priceId: item.priceId || '',
    quantity: '',
    amount: item.amount || '',
    type: LineItemType.Product,
    isEditing: item.isEditing || false,
    isValid: item.isValid || false,
    shouldAddPrice: item.shouldAddPrice || false,
    description: item.description || '',
    features: item.features || [],
    imageUrl: item.imageUrl || '',
    isImageUrlSetManually: item.isImageUrlSetManually || false,
  }));
};

export const ProductStoreEditor = ({
  handleChange,
  handleBlur,
  setFieldValue,
  values,
  errors,
  touched,
  setErrors,
  setTouched,
}: FormBodyAPI<ProductStoreSchema>) => {
  const { data: products = [] } = useGetProductsQuery();
  const { data: portalPrices = [] } = useGetPricesByPortalQuery();
  const [openPaymentPreferenceModal, setOpenPaymentPreferenceModal] =
    useState(false);
  const [tiptapEditor, setTiptapEditor] = useState<Editor | null>(null);
  const [draftFeatures, setDraftFeature] = React.useState<string[]>();
  const { basePortalDomain } = useCustomDomains();
  const [activeFormSection, setActiveFormSection] =
    useState<ActiveFormSectionType | null>(null);
  const [activeLineItemIndex, setActiveLineItemIndex] = useState<number | null>(
    null,
  );
  // To store the state of line items when when toggles are turned on and off
  const [draftLineItemsState, setDraftLineItemsState] = React.useState<{
    oneTimeProducts: ProductStoreLineItem[];
    subscriptions: ProductStoreLineItem[];
  }>({
    oneTimeProducts: [],
    subscriptions: [],
  });
  const defaultCurrency = useDefaultCurrency();
  const [openCreateProductPopover, setOpenCreateProductPopover] =
    useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [selectedLineItemPriceId, setSelectedLineItemPriceId] = useState<
    string | null
  >(null);

  const isSubscriptionItemEditing = values.subscriptions.some(
    (s) => s.isEditing,
  );

  const isOneTimeItemEditing = values.oneTimeProducts.some((i) => i.isEditing);

  const isItemEditing = useMemo(() => {
    return isSubscriptionItemEditing || isOneTimeItemEditing;
  }, [isSubscriptionItemEditing, isOneTimeItemEditing]);
  const isValidOneTimeItem = useMemo(() => {
    return values.oneTimeProducts.every((item) => item.isValid);
  }, [values.oneTimeProducts]);
  const isValidSubscriptionItem = useMemo(() => {
    return values.subscriptions.every((item) => item.isValid);
  }, [values.subscriptions]);
  const [addProduct, { isLoading: isCreatingProductLoading }] =
    useAddProductMutation();

  const [updateProduct, { isLoading: isUpdateProductLoading }] =
    useUpdateProductMutation();

  const [deletePrice] = useDeletePriceMutation();
  const [addPrice, { isLoading: iSavePriceLoading }] =
    useAddPriceByProductIdMutation();
  const handleProductSelected = (
    lineItem: LineItemFormSchema,
    itemIndex: number,
  ) => {
    if (lineItem.type === LineItemType.Product) {
      const product = products.find((p) => p.id === lineItem.productId);

      if (product) {
        const productImage = product.imageUrls?.at(0);

        // If imageUrl was not set manually, update or clear it

        if (productImage) {
          setFieldValue(`oneTimeProducts[${itemIndex}].imageUrl`, productImage);
        } else {
          // Clear the image if new product has no image

          setFieldValue(`oneTimeProducts[${itemIndex}].imageUrl`, '');
        }

        if (values.oneTimeProducts[itemIndex].isImageUrlSetManually) {
          setFieldValue(
            `oneTimeProducts[${itemIndex}].imageUrl`,
            values.oneTimeProducts[itemIndex].imageUrl,
          );
        }

        // If someone has already entered a description, we don't want to override it
        if (
          !values.oneTimeProducts[itemIndex].description ||
          values.oneTimeProducts[itemIndex].description === '' ||
          // Tiptap actually initializes with a <p></p> tag, the other values
          // are just defensive programming
          values.oneTimeProducts[itemIndex].description === '<p></p>'
        ) {
          tiptapEditor?.commands.setContent(product.description || '<p></p>');
          setFieldValue(
            `oneTimeProducts[${itemIndex}].description`,
            product.description,
          );
        }
      }
    }
  };
  const handleSubscriptionSelected = (
    lineItem: LineItemFormSchema,
    itemIndex: number,
  ) => {
    if (lineItem.type === LineItemType.Product) {
      const product = products.find((p) => p.id === lineItem.productId);
      if (product) {
        // If someone has already entered a description, we don't want to override it
        if (
          !values.subscriptions[itemIndex].description ||
          values.subscriptions[itemIndex].description === '' ||
          // Tiptap actually initializes with a <p></p> tag, the other values
          // are just defensive programming
          values.subscriptions[itemIndex].description === '<p></p>'
        ) {
          tiptapEditor?.commands.setContent(product.description || '<p></p>');
          setFieldValue(
            `subscriptions[${itemIndex}].description`,
            product.description,
          );
        }
      }
    }
  };

  const handleCancelLineItemEdit = (itemIndex: number) => {
    tiptapEditor?.commands.setContent('');
    setFieldValue(`oneTimeProducts[${itemIndex}].isImageUrlSetManually`, false);
  };

  const handleRemoveSubscriptionLineItem = (itemIndex: number) => {
    tiptapEditor?.commands.setContent('');
    if (draftFeatures) {
      setFieldValue(`subscriptions[${itemIndex}].features`, draftFeatures);
    }

    setDraftFeature([]);
  };

  const handleOpenPaymentPreferenceModal = () => {
    setOpenPaymentPreferenceModal(true);
  };

  const handleOpenCreateProductPopover = (
    itemIndex: number,
    productId?: string,
    priceId?: string,
  ) => {
    setSelectedProductId(productId || null);
    setActiveLineItemIndex(itemIndex);
    setSelectedLineItemPriceId(priceId || null);
    setOpenCreateProductPopover(true);
  };

  const handleClosePaymentPreferenceModal = () => {
    setOpenPaymentPreferenceModal(false);
  };

  const totalAmount = InvoiceUtils.GetTotalAmount(
    values.oneTimeProducts.map((item) => ({
      id: item.id || '',
      name: item.name || '',
      productId: item.productId || '',
      priceId: item.priceId || '',
      quantity: '',
      amount: item.amount || '',
      type: LineItemType.Product,
      isEditing: item.isEditing || false,
      isValid: item.isValid || false,
      shouldAddPrice: item.shouldAddPrice || false,
      description: item.description || '',
    })),
    TAX_PERCENTAGE,
  );

  const handleAddLineItem = (
    fieldName: 'subscriptions' | 'oneTimeProducts',
  ) => {
    const newLineItem = {
      name: '',
      productId: '',
      type: LineItemType.Product,
      priceId: '',
      id: v4(),
      quantity: '1',
      amount: '',
    };
    const items = values[fieldName]; // Would be either subscriptions or oneTimeProducts
    setFieldValue(fieldName, items.length > 0 ? items : [newLineItem]);
  };

  const handleProductSelectInLineItem = (
    product: Product,
    selectedPriceId?: string,
  ) => {
    if (!product) return;
    if (activeLineItemIndex === null) return;

    const isSubscription =
      activeFormSection === ActiveFormSectionType.Subscriptions;
    const isOneTimeProducts =
      activeFormSection === ActiveFormSectionType.OneTimeProducts;
    const isEditMode = !!selectedProductId;

    // Get the correct line items array for the current section
    const formattedLineItems = isSubscription
      ? getFormattedLineItems(values.subscriptions)
      : getFormattedLineItems(values.oneTimeProducts);

    // Get the updated array using the utility
    const updatedLineItems = getUpdatedLineItemAfterProductCreated({
      lineItems: formattedLineItems,
      activeLineItemIndex,
      product,
      isEditMode,
      selectedPriceId,
      portalPrices,
      mode: LineItemEditorPricingMode.subscriptionsStore,
    });

    // Patch extra fields for the selected item.
    if (updatedLineItems[activeLineItemIndex]) {
      updatedLineItems[activeLineItemIndex] = {
        ...updatedLineItems[activeLineItemIndex],
        name: product.name ?? '',
        // Subscriptions can't carry a header image (backend rejects it on
        // publish), so only inherit the service's image for one-time products
        // and clear any stale value the item may already hold.
        ...(isSubscription
          ? { imageUrl: undefined }
          : product?.imageUrls.at(0)
            ? { imageUrl: product.imageUrls.at(0) }
            : {}),
      };
    }

    if (isSubscription) {
      setFieldValue('subscriptions', updatedLineItems);
    }
    if (isOneTimeProducts) {
      setFieldValue('oneTimeProducts', updatedLineItems);
    }

    setActiveLineItemIndex(null);
    setSelectedProductId(null);
  };

  const handleAddProduct = async (product: ServiceSchema) => {
    const payload: ProductFormType<'new'> = {
      ...product,
    };
    const newProduct = await addProduct({
      product: payload,
    }).unwrap();

    return { data: newProduct };
  };

  const handleUpdateProduct = async (product: ServiceSchema) => {
    const payload: ProductFormType<'edit'> = {
      ...product,
      id: selectedProductId || '',
    };
    const updatedProduct = await updateProduct({ payload }).unwrap();
    return { data: updatedProduct };
  };

  const handlePriceRemove = async (data: ProductPriceTableRowModal) => {
    if (!selectedProductId || !data.id) return;
    await deletePrice({ productId: selectedProductId, priceId: data.id });
    return;
  };

  const handleAddPrice = async (data: CreatePriceInput) => {
    const response = await addPrice(data).unwrap();
    return response;
  };

  return (
    <div className="flex flex-1 min-h-0">
      <div className="w-full md:max-w-[422px] overflow-auto [scrollbar-gutter:stable] px-4 sm:px-6 box-border">
        <div className="flex flex-col gap-6 pb-6">
          <div
            className={clsx({
              'opacity-25 pointer-events-none': isItemEditing,
            })}
          >
            <Heading className="text-xl font-medium text-dark-black py-6">
              Name
            </Heading>
            <TextField
              name="displayName"
              sizeVariant="standard"
              onChange={handleChange}
              value={values.displayName}
              onBlur={handleBlur}
              onFocus={() =>
                setActiveFormSection(ActiveFormSectionType.DisplayName)
              }
              fullWidth
              variant="outlined"
              error={Boolean(touched.displayName && errors.displayName)}
              helperText={
                touched.displayName && errors.displayName
                  ? errors.displayName
                  : ''
              }
              autoFocus
              autoComplete="off"
              placeholder="Store name"
              InputProps={{
                classes: {
                  input: 'text-[14px]',
                },
              }}
            />
          </div>
          <div
            className={clsx({
              'opacity-25 pointer-events-none': isOneTimeItemEditing,
            })}
          >
            <ProductStoreLineItemCard
              title="Plans"
              canAddItems={values.productStorePreferences.showOnlySubscriptions}
              isEditing={isItemEditing}
              allowToolTipInfo={
                !values.productStorePreferences.showOnlyOneTimeProducts &&
                values.productStorePreferences.showOnlySubscriptions
              }
              onToggleChange={(showSubscriptions) => {
                if (!values.productStorePreferences.showOnlyOneTimeProducts)
                  return;
                setFieldValue(
                  'productStorePreferences.showOnlySubscriptions',
                  showSubscriptions,
                );
                if (showSubscriptions) {
                  setTouched(
                    {
                      ...touched,
                      subscriptions: [],
                    },
                    false,
                  );
                  setErrors({
                    ...errors,
                    subscriptions: [],
                  });
                  // Restore subscriptions from backup
                  if (draftLineItemsState.subscriptions.length > 0) {
                    setFieldValue(
                      'subscriptions',
                      draftLineItemsState.subscriptions,
                    );
                    setDraftLineItemsState((prev) => ({
                      ...prev,
                      subscriptions: [],
                    }));
                  } else {
                    handleAddLineItem('subscriptions');
                  }
                } else {
                  setDraftLineItemsState((prev) => ({
                    ...prev,
                    subscriptions: values.subscriptions.filter(
                      (s) => s.productId,
                    ),
                  }));
                  setFieldValue('subscriptions', []);
                }
              }}
            >
              <LineItemEditor<ProductStoreSchema, true>
                fieldName={'name'}
                allowRecurringPrices
                getLineItems={(o) => getFormattedLineItems(o.subscriptions)}
                showAllIntervalPrices
                formikLineItemFieldName="subscriptions"
                formikAmountFieldName="amount"
                mode={LineItemEditorPricingMode.subscriptionsStore}
                onLineItemSelected={handleSubscriptionSelected}
                onResetLineItem={handleRemoveSubscriptionLineItem}
                label="Services"
                tooltipDescription=""
                buttonLabel="Add service"
                allowCreatingNewProduct={false}
                allowCreatingNewPrice={false}
                lineItemsInCents={false}
                addProductLabel={''}
                allowAddNewOneTimeItem={false}
                restrictProductPriceReuse
                showCreateNewProductItem
                renderPresentationComponent={(props) => (
                  <ProductStorePresentation
                    {...props}
                    description={
                      values.subscriptions[props.itemIndex].description || ''
                    }
                    placeholder="Recurring service"
                    onUpdateTipTapEditor={setTiptapEditor}
                    allowCoverImage={false}
                    allowFeature
                    isItemEditing={isItemEditing}
                    onEditDraftFeature={setDraftFeature}
                    onSetActiveFormSection={() => {
                      setActiveFormSection(ActiveFormSectionType.Subscriptions);
                    }}
                    onOpenCreateProductPopover={handleOpenCreateProductPopover}
                  />
                )}
                isQuantityRequired={false}
                maxLineItems={MAX_SUBSCRIPTIONS_PRODUCTS}
                products={products}
                portalPrices={portalPrices}
              />
            </ProductStoreLineItemCard>
          </div>
          <div
            className={clsx({
              'opacity-25 pointer-events-none': isSubscriptionItemEditing,
              // When adding a new product, the autocomplete is positioned absolutely and overlaps below the title.
              // To fix this, we add extra padding to create proper spacing between the title and the autocomplete.
              'pt-12':
                !isSubscriptionItemEditing &&
                !isValidSubscriptionItem &&
                values.subscriptions.length > 1,
            })}
          >
            <ProductStoreLineItemCard
              title="One-time purchases"
              canAddItems={
                values.productStorePreferences.showOnlyOneTimeProducts
              }
              isEditing={isItemEditing}
              allowToolTipInfo={
                !values.productStorePreferences.showOnlySubscriptions &&
                values.productStorePreferences.showOnlyOneTimeProducts
              }
              onToggleChange={(showOneTimeProducts) => {
                if (!values.productStorePreferences.showOnlySubscriptions)
                  return;
                setFieldValue(
                  'productStorePreferences.showOnlyOneTimeProducts',
                  showOneTimeProducts,
                );
                if (showOneTimeProducts) {
                  setTouched(
                    {
                      ...touched,
                      oneTimeProducts: [],
                    },
                    false,
                  );

                  setErrors({
                    ...errors,
                    oneTimeProducts: [],
                  });

                  if (draftLineItemsState.oneTimeProducts.length > 0) {
                    setFieldValue(
                      'oneTimeProducts',
                      draftLineItemsState.oneTimeProducts,
                    );
                    setDraftLineItemsState((prev) => ({
                      ...prev,
                      oneTimeProducts: [],
                    }));
                  } else {
                    handleAddLineItem('oneTimeProducts');
                  }
                } else {
                  setDraftLineItemsState((prev) => ({
                    ...prev,
                    oneTimeProducts: values.oneTimeProducts.filter(
                      (otp) => otp.productId,
                    ),
                  }));

                  setFieldValue('oneTimeProducts', []);
                }
              }}
            >
              <LineItemEditor<ProductStoreSchema, false>
                fieldName="name"
                allowRecurringPrices={false}
                getLineItems={(o) => getFormattedLineItems(o.oneTimeProducts)}
                formikLineItemFieldName="oneTimeProducts"
                formikAmountFieldName="amount"
                mode={LineItemEditorPricingMode.OneTimeStore}
                onLineItemSelected={handleProductSelected}
                onResetLineItem={handleCancelLineItemEdit}
                label="Services"
                buttonLabel="Add service"
                tooltipDescription=""
                allowCreatingNewProduct={false}
                allowCreatingNewPrice={false}
                lineItemsInCents={false}
                addProductLabel={''}
                allowAddNewOneTimeItem={false}
                restrictProductPriceReuse
                showCreateNewProductItem
                renderPresentationComponent={(props) => (
                  <ProductStorePresentation
                    {...props}
                    productStoreImageUrl={
                      values.oneTimeProducts[props.itemIndex].imageUrl || ''
                    }
                    description={
                      values.oneTimeProducts[props.itemIndex].description || ''
                    }
                    placeholder="One-time service"
                    onUpdateTipTapEditor={setTiptapEditor}
                    allowCoverImage
                    allowFeature={false}
                    isItemEditing={isItemEditing}
                    onSetActiveFormSection={() => {
                      setActiveFormSection(
                        ActiveFormSectionType.OneTimeProducts,
                      );
                    }}
                    onOpenCreateProductPopover={handleOpenCreateProductPopover}
                  />
                )}
                isQuantityRequired={false}
                maxLineItems={MAX_ONE_TIME_PRODUCTS}
                products={products}
                portalPrices={portalPrices}
              />
            </ProductStoreLineItemCard>
          </div>
          <div
            className={clsx({
              'opacity-25 pointer-events-none': isItemEditing,
              // When adding a new product, the autocomplete is positioned absolutely and overlaps below the title.
              // To fix this, we add extra padding to create proper spacing between the title and the autocomplete.
              'pt-12':
                !isOneTimeItemEditing &&
                !isValidOneTimeItem &&
                values.oneTimeProducts.length > 1,
            })}
          >
            <Heading className="text-xl font-medium text-dark-black py-6">
              Payment preferences
            </Heading>
            <div className="flex flex-row gap-3">
              <Button
                onClick={handleOpenPaymentPreferenceModal}
                variant="secondary"
                prefixIcon="Edit"
                label="Manage payment preferences"
                className="justify-start text-black-heading hover:text-gray-small [&>p]:text-[12px] p-0 font-medium hover:bg-transparent border-0 [&>svg]:pr-1"
              />
            </div>
          </div>
          {openPaymentPreferenceModal && (
            <ManageManualPaymentSetting
              paymentPreferences={
                values.productStorePreferences.paymentPreferences
              }
              isOpen={openPaymentPreferenceModal}
              onHandleClose={handleClosePaymentPreferenceModal}
              positiveAction
              onHandleSuccess={handleClosePaymentPreferenceModal}
              onUpdatePaymentPreferences={(preferences) => {
                setFieldValue(
                  'productStorePreferences.paymentPreferences',
                  preferences,
                );
              }}
              totalAmount={totalAmount}
            />
          )}
        </div>
      </div>
      <div className="@container bg-gray-100  w-full hidden md:block ">
        <ProductStorePreview
          basePortalDomain={basePortalDomain}
          productStore={values}
          activeFormSection={activeFormSection}
          products={products}
          prices={portalPrices}
        />
      </div>
      {/* Render the popover at the parent level */}
      <CreateServicePopover
        open={openCreateProductPopover}
        onClose={() => setOpenCreateProductPopover(false)}
        defaultCurrency={defaultCurrency}
        showRecurringField={
          activeFormSection === ActiveFormSectionType.Subscriptions
        }
        mode={
          activeFormSection === ActiveFormSectionType.Subscriptions
            ? LineItemEditorPricingMode.subscriptionsStore
            : LineItemEditorPricingMode.OneTimeStore
        }
        productId={selectedProductId || ''}
        formType={selectedProductId ? 'edit' : 'new'}
        lineItems={
          activeFormSection === ActiveFormSectionType.Subscriptions
            ? getFormattedLineItems(values.subscriptions)
            : getFormattedLineItems(values.oneTimeProducts)
        }
        onProductSelectInLineItem={handleProductSelectInLineItem}
        selectedLineItemPriceId={selectedLineItemPriceId || ''}
        products={products}
        portalPrices={portalPrices}
        onCreateProduct={handleAddProduct}
        onUpdateProduct={handleUpdateProduct}
        isLoading={isCreatingProductLoading || isUpdateProductLoading}
        isSavePriceLoading={iSavePriceLoading}
        onDeletePrice={handlePriceRemove}
        onAddPrice={handleAddPrice}
      />
    </div>
  );
};
